/**
 * Lógica pura do filtro de período global do dashboard.
 *
 * Extraído de `TelaDashboard.tsx` para permitir teste sem montar componente React. As funções que
 * dependem da data recebem `hoje` como parâmetro em vez de chamar `new Date()` por dentro — senão o
 * teste passaria ou falharia conforme o dia em que rodasse.
 */

// ---------------------------------------------------------------------------
// Tipos e constantes
// ---------------------------------------------------------------------------

type VisaoEnvios = 'diaria' | 'semanal' | 'mensal' | 'trimestral' | 'anual';

/**
 * Período global do dashboard, em semântica de **calendário**.
 *
 * ## Por que calendário e não janela móvel
 *
 * A primeira versão usava janelas móveis — "últimos 90 dias" em vez de "este trimestre". Parecia
 * mais seguro, porque janela móvel nunca começa vazia. Mas está errado para o uso real: meta de
 * vendas é fechada por trimestre e por mês de calendário. Um vendedor que pergunta "como estou no
 * trimestre" não quer os últimos 90 dias corridos, quer o trimestre que a diretoria vai cobrar.
 *
 * Uma janela móvel de 90 dias em 14 de agosto começa em 17 de maio, e portanto mistura o segundo
 * trimestre com o terceiro. O número que sai dali não corresponde a nenhuma meta, e é pior que
 * inútil: parece oficial e não é.
 *
 * ## O custo dessa escolha, e como está tratado
 *
 * Todo dia 1º de trimestre o dashboard abre quase vazio. Isso é correto — a apuração realmente
 * começou do zero — mas assusta quem não esperava. Por isso a tela mostra o intervalo ativo por
 * extenso, a contagem filtrada contra o total, e um estado vazio que explica a situação e oferece
 * "ver todo o histórico" em um clique.
 */
type TipoPeriodo =
  'tudo' | 'hoje' | 'semanaAtual' | 'mesAtual' | 'trimestre' | 'anoAtual' | 'personalizado';

/**
 * Período padrão ao abrir o dashboard.
 *
 * Trimestre, por decisão do usuário: é o ciclo em que a empresa fecha meta, então é a pergunta que o
 * vendedor faz primeiro ao abrir a tela. Qual trimestre vem selecionado é resolvido por
 * `trimestreCorrente`, que devolve o corrente.
 */
const PERIODO_PADRAO: TipoPeriodo = 'trimestre';

/**
 * Rótulos do seletor.
 *
 * Nomeados pelo ciclo, não pela quantidade de dias, porque é assim que a conversa comercial acontece
 * — ninguém pergunta "como foram os últimos 90 dias".
 */
const ROTULOS_PERIODO: Readonly<Record<TipoPeriodo, string>> = {
  tudo: 'Todo o histórico',
  hoje: 'Hoje',
  semanaAtual: 'Esta semana',
  mesAtual: 'Este mês',
  trimestre: 'Trimestre',
  anoAtual: 'Este ano',
  personalizado: 'Intervalo personalizado',
};

/**
 * Granularidade sugerida para o gráfico de evolução, conforme o período.
 *
 * Agrupar por mês quando o período é "hoje" produz uma coluna solitária; agrupar por dia quando o
 * período é "todo o histórico" produz centenas de colunas ilegíveis. A sugestão evita as duas
 * situações, e o usuário pode sobrescrever — é sugestão, não imposição.
 */
const GRANULARIDADE_SUGERIDA: Readonly<Record<TipoPeriodo, VisaoEnvios>> = {
  tudo: 'mensal',
  hoje: 'diaria',
  semanaAtual: 'diaria',
  mesAtual: 'diaria',
  trimestre: 'semanal',
  anoAtual: 'mensal',
  personalizado: 'mensal',
};

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

/**
 * Data em `AAAA-MM-DD` no fuso **local**.
 *
 * `toISOString()` converte para UTC e adianta a data em até um dia no Brasil: à noite,
 * `new Date().toISOString()` já devolve o dia seguinte. Uma proposta emitida hoje cairia fora do
 * filtro de hoje, e o vendedor não encontraria o que acabou de publicar.
 */
function formatarDataIso(d: Date): string {
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${String(ano)}-${mes}-${dia}`;
}

/**
 * Converte `AAAA-MM-DD` para `DD/MM/AAAA` por manipulação de string.
 *
 * Sem `new Date` pelo mesmo motivo de fuso: `new Date('2026-08-14')` é meia-noite UTC, que em
 * Brasília é 21h do dia 13, e a data exibida sairia um dia atrasada.
 */
function formatarDataBr(iso: string): string {
  const partes = iso.split('-');
  const ano = partes[0] ?? '0000';
  const mes = partes[1] ?? '01';
  const dia = partes[2] ?? '01';
  return `${dia}/${mes}/${ano}`;
}

// ---------------------------------------------------------------------------
// Intervalo
// ---------------------------------------------------------------------------

/** Intervalo efetivo, com datas em `AAAA-MM-DD`. */
interface Intervalo {
  readonly de: string;
  readonly ate: string;
}

// ---------------------------------------------------------------------------
// Trimestres do ano civil
// ---------------------------------------------------------------------------

/**
 * Bordas dos quatro trimestres do ano civil.
 *
 * | Trimestre | Início | Fim |
 * | --- | --- | --- |
 * | Q1 | 1º de janeiro | 31 de março |
 * | Q2 | 1º de abril | 30 de junho |
 * | Q3 | 1º de julho | 30 de setembro |
 * | Q4 | 1º de outubro | 31 de dezembro |
 *
 * Declaradas como tabela em vez de calculadas em cada ponto de uso. O cálculo é trivial — um
 * `Math.ceil(mes / 3)` resolve — mas espalhá-lo é como as bordas divergem: basta um `ceil` virar
 * `floor` em um dos lugares para o gráfico e os cards discordarem sobre onde o trimestre começa, e o
 * número sair errado sem nada quebrar. Tabela é conferível de relance.
 *
 * A nomenclatura usa `Q` (quarter) porque é como a empresa fecha meta. A mesma letra vale para a
 * chave interna (`2026-Q3`) e para o rótulo na tela (`Q3/2026`), de propósito — convenção única
 * evita que alguém compare chave com rótulo e conclua que são coisas diferentes.
 */
const TRIMESTRES = [
  { numero: 1, mesInicial: 1, mesFinal: 3, diaFinal: 31, meses: 'jan a mar' },
  { numero: 2, mesInicial: 4, mesFinal: 6, diaFinal: 30, meses: 'abr a jun' },
  { numero: 3, mesInicial: 7, mesFinal: 9, diaFinal: 30, meses: 'jul a set' },
  { numero: 4, mesInicial: 10, mesFinal: 12, diaFinal: 31, meses: 'out a dez' },
] as const;

/** Identificador de um trimestre, no formato `AAAA-Qn`. Exemplo: `2026-Q3`. */
type ChaveTrimestre = string;

/** Monta a chave de um trimestre. */
function chaveTrimestre(ano: number, numero: number): ChaveTrimestre {
  return `${String(ano)}-Q${String(numero)}`;
}

/**
 * Interpreta a chave de um trimestre.
 *
 * Devolve `null` para chave malformada, em vez de presumir Q1: presumir esconderia o defeito e o
 * usuário veria dados de um trimestre que não pediu, sem aviso algum.
 */
function interpretarChaveTrimestre(chave: ChaveTrimestre): { ano: number; numero: number } | null {
  const casamento = /^(\d{4})-Q([1-4])$/.exec(chave);
  if (casamento === null) return null;
  const ano = parseInt(casamento[1] ?? '', 10);
  const numero = parseInt(casamento[2] ?? '', 10);
  if (Number.isNaN(ano) || Number.isNaN(numero)) return null;
  return { ano, numero };
}

/** Trimestre de calendário (1 a 4) do mês informado, em base 1. */
function trimestreDoMes(mesBase1: number): number {
  return TRIMESTRES.find((t) => mesBase1 >= t.mesInicial && mesBase1 <= t.mesFinal)?.numero ?? 1;
}

/** Chave do trimestre corrente na data informada. */
function trimestreCorrente(hoje: Date): ChaveTrimestre {
  return chaveTrimestre(hoje.getFullYear(), trimestreDoMes(hoje.getMonth() + 1));
}

/**
 * Bordas de um trimestre, em `AAAA-MM-DD`.
 *
 * O fim é o último dia do trimestre, **exceto** quando o trimestre ainda está em curso: aí o fim é
 * hoje. Incluir dias que não aconteceram faria a média diária dividir por eles e reportar
 * desempenho abaixo do real — em 5 de outubro, um Q4 fechado em 31 de dezembro diluiria o resultado
 * por quase três meses inexistentes.
 *
 * Trimestre inteiramente no futuro devolve `null`: não há período a exibir, e mostrar zero
 * convidaria à leitura de que nada foi vendido.
 */
function intervaloDoTrimestre(chave: ChaveTrimestre, hoje: Date): Intervalo | null {
  const interpretado = interpretarChaveTrimestre(chave);
  if (interpretado === null) return null;

  const definicao = TRIMESTRES.find((t) => t.numero === interpretado.numero);
  if (definicao === undefined) return null;

  const ano = String(interpretado.ano);
  const de = `${ano}-${String(definicao.mesInicial).padStart(2, '0')}-01`;
  const fim = `${ano}-${String(definicao.mesFinal).padStart(2, '0')}-${String(definicao.diaFinal)}`;
  const hojeStr = formatarDataIso(hoje);

  if (de > hojeStr) return null;

  return { de, ate: fim > hojeStr ? hojeStr : fim };
}

/**
 * Trimestres que o seletor deve oferecer: os presentes nos dados, mais o corrente.
 *
 * O corrente entra mesmo sem proposta alguma, porque é o padrão da tela — sua ausência faria o
 * seletor abrir em um trimestre que o usuário não escolheu. Ordem decrescente, porque o interesse
 * decai com a idade do trimestre.
 */
function trimestresDisponiveis(
  itens: readonly { readonly emitidaEm: string }[],
  hoje: Date,
): readonly ChaveTrimestre[] {
  const chaves = new Set<ChaveTrimestre>([trimestreCorrente(hoje)]);

  for (const item of itens) {
    const ano = parseInt(item.emitidaEm.slice(0, 4), 10);
    const mes = parseInt(item.emitidaEm.slice(5, 7), 10);
    if (Number.isNaN(ano) || Number.isNaN(mes)) continue;
    chaves.add(chaveTrimestre(ano, trimestreDoMes(mes)));
  }

  return [...chaves].sort((a, b) => b.localeCompare(a));
}

/**
 * Rótulo de um trimestre para a tela.
 *
 * Traz os meses por extenso porque "Q3" exige que o leitor faça a conta, e marca o corrente para o
 * usuário saber onde está sem comparar com a data de hoje.
 */
function rotuloTrimestre(chave: ChaveTrimestre, hoje: Date): string {
  const interpretado = interpretarChaveTrimestre(chave);
  if (interpretado === null) return chave;

  const definicao = TRIMESTRES.find((t) => t.numero === interpretado.numero);
  const meses = definicao?.meses ?? '';
  const sufixo = chave === trimestreCorrente(hoje) ? ' — atual' : '';
  return `Q${String(interpretado.numero)}/${String(interpretado.ano)} (${meses})${sufixo}`;
}

// ---------------------------------------------------------------------------
// Cálculo do intervalo
// ---------------------------------------------------------------------------

/**
 * Calcula o intervalo do período selecionado.
 *
 * Devolve `null` quando não há filtro: período `'tudo'`, ou `'personalizado'` sem nenhuma data —
 * caso em que filtrar por um intervalo inventado esconderia dados sem o usuário ter pedido.
 *
 * A semana começa na **segunda-feira**, não no domingo. É a convenção ISO 8601 e a que corresponde à
 * semana de trabalho: quem pergunta "como foi esta semana" na quarta-feira não está contando o
 * domingo anterior como início do ciclo comercial.
 */
function calcularIntervalo(
  tipo: TipoPeriodo,
  de: string,
  ate: string,
  hoje: Date,
  trimestreSelecionado: ChaveTrimestre = '',
): Intervalo | null {
  if (tipo === 'tudo') return null;

  if (tipo === 'personalizado') {
    if (de === '' && ate === '') return null;
    // Lado vazio vira limite aberto, para o usuário poder informar só um dos extremos: "de julho em
    // diante" é uma pergunta legítima, e exigir os dois campos forçaria uma data arbitrária.
    return { de: de === '' ? '0000-01-01' : de, ate: ate === '' ? '9999-12-31' : ate };
  }

  if (tipo === 'trimestre') {
    const chave = trimestreSelecionado === '' ? trimestreCorrente(hoje) : trimestreSelecionado;
    return intervaloDoTrimestre(chave, hoje);
  }

  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const dia = hoje.getDate();

  // Fim sempre hoje: incluir dias futuros do período faria a média diária dividir por dias que
  // ainda não aconteceram.
  const ateStr = formatarDataIso(hoje);

  if (tipo === 'hoje') {
    return { de: ateStr, ate: ateStr };
  }

  if (tipo === 'semanaAtual') {
    const diaDaSemana = hoje.getDay(); // 0 domingo … 6 sábado
    // Domingo (0) pertence à semana que começou na segunda anterior, seis dias atrás.
    const recuo = diaDaSemana === 0 ? 6 : diaDaSemana - 1;
    return { de: formatarDataIso(new Date(ano, mes, dia - recuo)), ate: ateStr };
  }

  if (tipo === 'mesAtual') {
    return { de: formatarDataIso(new Date(ano, mes, 1)), ate: ateStr };
  }

  // anoAtual
  return { de: formatarDataIso(new Date(ano, 0, 1)), ate: ateStr };
}

/**
 * Nome do período ativo por extenso, para a tela declarar o que está somando.
 *
 * Um card mostrando "12 propostas" sem dizer de quando convida à conclusão errada, e a conclusão
 * errada aqui é sobre o próprio desempenho de quem lê.
 */
function descreverPeriodo(
  tipo: TipoPeriodo,
  intervalo: Intervalo | null,
  hoje: Date,
  trimestreSelecionado: ChaveTrimestre = '',
): string {
  if (intervalo === null) {
    if (tipo === 'trimestre') return 'um trimestre que ainda não começou';
    return 'todo o histórico';
  }

  if (tipo === 'personalizado') {
    const inicioAberto = intervalo.de === '0000-01-01';
    const fimAberto = intervalo.ate === '9999-12-31';
    if (inicioAberto) return `até ${formatarDataBr(intervalo.ate)}`;
    if (fimAberto) return `de ${formatarDataBr(intervalo.de)} em diante`;
    return `${formatarDataBr(intervalo.de)} a ${formatarDataBr(intervalo.ate)}`;
  }

  if (tipo === 'hoje') return `hoje, ${formatarDataBr(intervalo.ate)}`;

  if (tipo === 'trimestre') {
    const chave = trimestreSelecionado === '' ? trimestreCorrente(hoje) : trimestreSelecionado;
    const interpretado = interpretarChaveTrimestre(chave);
    const nome =
      interpretado === null
        ? 'trimestre'
        : `Q${String(interpretado.numero)}/${String(interpretado.ano)}`;
    const emCurso = chave === trimestreCorrente(hoje);
    // Trimestre em curso avisa que a apuração não fechou, senão o número parece final e é comparado
    // com trimestres completos como se fosse.
    const nota = emCurso ? ', em curso' : '';
    return `${nome}, de ${formatarDataBr(intervalo.de)} a ${formatarDataBr(intervalo.ate)}${nota}`;
  }

  return `${ROTULOS_PERIODO[tipo].toLowerCase()}, de ${formatarDataBr(intervalo.de)} a ${formatarDataBr(intervalo.ate)}`;
}

// ---------------------------------------------------------------------------
// Filtro
// ---------------------------------------------------------------------------

/**
 * Filtra por intervalo comparando string no formato `AAAA-MM-DD`.
 *
 * Comparação lexicográfica funciona nesse formato porque os campos são de largura fixa e vão do mais
 * significativo ao menos. Evita construir `Date` por item, e com isso evita o erro de fuso de uma
 * vez — não há conversão onde errar.
 *
 * As duas bordas entram: proposta emitida exatamente no primeiro ou no último dia pertence ao
 * período.
 */
function filtrarPorIntervalo<T extends { readonly emitidaEm: string }>(
  itens: readonly T[],
  intervalo: Intervalo | null,
): readonly T[] {
  if (intervalo === null) return itens;
  return itens.filter((i) => i.emitidaEm >= intervalo.de && i.emitidaEm <= intervalo.ate);
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  calcularIntervalo,
  chaveTrimestre,
  descreverPeriodo,
  filtrarPorIntervalo,
  formatarDataBr,
  formatarDataIso,
  GRANULARIDADE_SUGERIDA,
  interpretarChaveTrimestre,
  intervaloDoTrimestre,
  PERIODO_PADRAO,
  ROTULOS_PERIODO,
  rotuloTrimestre,
  TRIMESTRES,
  trimestreCorrente,
  trimestreDoMes,
  trimestresDisponiveis,
};

export type { ChaveTrimestre, Intervalo, TipoPeriodo, VisaoEnvios };
